<template>
  <validation-observer ref="formValidator" class="minute-form">
    <section-title
      class="minute-form__subtitle minute-form__subtitle--small"
      content="Información del Estudiante"
    />

    <validation-provider
      v-slot="{ errors }"
      name="Número de SIE"
      rules="required"
      :value="form.students"
    >
      <student-sie-search
        v-model="form.students"
        :limit="1"
        :statuses-allowed="allowedStudentStatuses"
        :student-search-handler="searchStudents"
        :disabled="readonly"
        :error="errors[0]"
        custom-error-msg="El estudiante es requerido y El estatus debe ser Elegible o Servido."
      />
    </validation-provider>

    <div v-if="!selectedStudent" class="minute-form__empty-student">
      <icon-view
        icon="fas fa-user-circle fa-lg text-orange"
        variant="custom-icon-view"
        icon-background="white"
        title="Estudiante Seleccionado"
      />
      <empty
        class="minute-form__empty-student-message"
        text="No hay Estudiante Seleccionado"
      />
    </div>

    <template v-if="selectedStudent">
      <section-title
        class="minute-form__subtitle minute-form__subtitle--small mt-3"
        content="Información de cita"
      />

      <div class="row">
        <div class="col-12 col-md-4">
          <custom-drop-down
            v-model="form.academicYear"
            label="Año Académico:"
            :options="academicYears"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :disabled="readonly"
          />
        </div>
        <div class="col-12 col-md-4">
          <validation-provider
            v-slot="{ errors }"
            name="Escuela"
            rules="required"
            :value="form.school"
          >
            <custom-drop-down
              v-model="form.school"
              label="Escuela:"
              :options="schools"
              fieldkey="id"
              fieldname="name"
              :objectkey="schoolObjectKey"
              placeholder="Seleccione"
              :disabled="readonly"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-4">
          <validation-provider
            v-slot="{ errors }"
            name="Pueblo"
            rules="required"
            :value="form.town"
          >
            <custom-drop-down
              v-model="form.town"
              label="Pueblo:"
              :options="towns"
              fieldkey="id"
              fieldname="name"
              :objectkey="townObjectKey"
              placeholder="Seleccione"
              :disabled="readonly"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-4 mt-2">
          <validation-provider
            v-slot="{ errors }"
            name="Fecha"
            rules="required"
            :value="form.appointmentDate"
          >
            <custom-date-picker
              v-model="form.appointmentDate"
              label="Fecha:"
              format="dd/MM/yyyy"
              placeholder="DD/MM/YYYY"
              :disabled="readonly"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-4 mt-2">
          <span class="minute-form__label">
            ¿Existe una cita de evaluación coordinada?
          </span>
          <custom-radio
            v-model="form.hasCoordinatedAppointment"
            name="coordinated-evaluation"
            :options="yesNoOptions"
            :change-event="handleHasCoordinatedAppointmentChange"
            :is-disabled="readonly"
            custom-error-msg=""
          />
        </div>
        <div
          v-if="form.hasCoordinatedAppointment === true"
          class="col-12 col-md-4 mt-2"
        >
          <validation-provider
            v-slot="{ errors }"
            name="Seleccione cita"
            rules="required"
            :value="
              readonly
                ? readonlyCoordinatedAppointmentLabel
                : form.coordinatedAppointment
            "
          >
            <div v-if="readonly" class="minute-form__readonly-appointment">
              <span class="minute-form__label">Seleccione cita:</span>
              <div class="minute-form__readonly-appointment-value">
                {{ readonlyCoordinatedAppointmentLabel }}
              </div>
            </div>
            <custom-drop-down
              v-else
              v-model="form.coordinatedAppointment"
              label="Seleccione cita:"
              :options="coordinatedAppointments"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              :clearable="false"
              :disabled="readonly || !hasCoordinatedAppointments"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
              @input="handleCoordinatedAppointmentSelection"
            />
          </validation-provider>
          <span
            v-if="showNoCoordinatedAppointments"
            class="minute-form__empty-coordination"
          >
            No se encontraron coordinaciones en estado Coordinada o Confirmada
            para este estudiante.
          </span>
        </div>
      </div>

      <section-title
        class="minute-form__subtitle minute-form__subtitle--small mt-3"
        content="Propósito(s) de la reunión"
      />

      <validation-provider
        v-slot="{ errors }"
        name="Propósito(s) de la reunión"
        :value="selectedPurposes"
      >
        <div class="row minute-form__purposes">
          <div
            v-for="purpose in purposeOptions"
            :key="purpose.id"
            :class="getPurposeColumnClass(purpose)"
          >
            <div
              class="minute-form__purpose-option"
              :class="getPurposeOptionClass(purpose)"
            >
              <check-box
                v-model="purpose.selected"
                class="font-weight-light text-dark"
                :text="purpose.name"
                :disabled="readonly"
                @input="handlePurposeInput(purpose, $event)"
              />
              <custom-input
                v-if="isOtherPurpose(purpose) && purpose.selected"
                v-model="purpose.otherDescription"
                class="minute-form__other-purpose"
                :disabled="readonly"
                :error="otherPurposeValidationError"
                custom-error-msg="Debe ingresar una descripción"
                @input="clearOtherPurposeValidationError"
              />
            </div>
          </div>
        </div>
        <custom-error
          v-if="purposeValidationError || errors[0]"
          variant="no-border"
          :message="purposeValidationError || errors[0]"
        />
      </validation-provider>

      <div
        v-if="hasEvaluationAnalysisPurpose && (!readonly || selectedEvaluations.length)"
        class="minute-form__evaluations"
      >
        <p v-if="!readonly" class="minute-form__label">
          Evaluaciones Pendientes:
        </p>
        <div v-if="!readonly" class="minute-form__evaluation-row">
          <v-select
            v-model="selectedPendingEvaluation"
            class="minute-form__evaluation-select"
            :options="availablePendingEvaluations"
            label="name"
            placeholder="Seleccione"
          >
            <template #option="evaluation">
              <div class="minute-form__evaluation-option">
                <span class="minute-form__evaluation-option-name">
                  {{ evaluation.name }}
                </span>
                <span class="minute-form__evaluation-option-date">
                  {{ formatEvaluationDate(evaluation.evaluationDate) }}
                </span>
              </div>
            </template>
            <template #selected-option="evaluation">
              <span>{{ evaluation.name }}</span>
            </template>
          </v-select>
          <action-button
            class="minute-form__evaluation-add-button"
            i="fas fa-file-plus"
            text="Añadir"
            variant="success"
            @click="addPendingEvaluation"
          />
        </div>

        <span
          v-if="
            !readonly &&
              !isPendingEvaluationsLoading &&
              !pendingEvaluations.length
          "
          class="minute-form__empty-coordination"
        >
          No se encontraron evaluaciones para este estudiante.
        </span>

        <div
          v-if="selectedEvaluations.length"
          class="minute-form__evaluation-list"
        >
          <p class="minute-form__label mt-2">
            Evaluación(es) Seleccionada(s):
          </p>
          <div
            v-for="evaluation in selectedEvaluations"
            :key="evaluation.studentEvaluationId"
            class="minute-form__evaluation-row-item"
          >
            <div class="minute-form__evaluation-info">
              <div>
                <span class="font-weight-bold">Nombre de Evaluación:</span>
                {{ evaluation.name }}
              </div>
              <div>
                <span class="font-weight-bold">Fecha de Evaluación:</span>
                {{ formatEvaluationDate(evaluation.evaluationDate) }}
              </div>
            </div>
            <button
              v-if="!readonly"
              type="button"
              class="minute-form__evaluation-remove"
              @click="removePendingEvaluation(evaluation.studentEvaluationId)"
            >
              <i class="fal fa-times-circle" />
            </button>
          </div>
        </div>

        <custom-error
          v-if="evaluationValidationError"
          variant="no-border"
          :message="evaluationValidationError"
        />
      </div>
    </template>
  </validation-observer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ActionButton from 'ActionButton';
import CheckBox from 'CheckBox';
import CustomError from 'custom-error';
import CustomDatePicker from 'custom-date-picker';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import CustomRadio from 'custom-radio';
import IconView from 'icon-view';
import SectionTitle from 'SectionTitle';
import Empty from '@/components/common/Empty.vue';
import StudentSieSearch from '@/components/student/StudentSieSearch.vue';
import alertMixin from '@/mixin/alert.mixin';
import academicYearService from '@/modules/common/academicyear.api';
import compuAppointmentService from '@/modules/compu-appointment/api';
import compuMinuteService from '@/modules/compu-minutes-editor/api';
import locationService from '@/services/api/location.api';
import StudentStatuses from '@/utils/constants/student/studentStatuses.js';
import moment from 'moment';

const EVALUATION_ANALYSIS_PURPOSE_ID = 2;

export default {
  name: 'GeneralInformationTab',
  mixins: [alertMixin],
  props: {
    initialData: {
      type: Object,
      default: null,
    },
    minuteId: {
      type: [Number, String],
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    ActionButton,
    CheckBox,
    CustomError,
    CustomDatePicker,
    CustomDropDown,
    CustomInput,
    CustomRadio,
    Empty,
    IconView,
    SectionTitle,
    StudentSieSearch,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      form: {
        students: [],
        academicYear: null,
        school: null,
        town: null,
        appointmentDate: null,
        hasCoordinatedAppointment: null,
        coordinatedAppointment: null,
        compuAppointmentId: null,
      },
      academicYears: [],
      schools: [],
      towns: [],
      locationsAcademicYearId: null,
      locationsFetchedAcademicYearId: null,
      purposeOptions: [],
      participants: [],
      availableSignatureOptions: [],
      participationTypeList: [],
      signatureCounter: 0,
      participantGroupNames: {
        0: 'Funcionario',
        1: 'Representante de Familia',
        4: 'Representante Programa de Educación Especial',
        5: 'Representante DEPR',
      },
      purposeValidationError: '',
      otherPurposeValidationError: '',
      evaluationValidationError: '',
      pendingEvaluations: [],
      selectedPendingEvaluation: null,
      selectedEvaluations: [],
      isPendingEvaluationsLoading: false,
      coordinatedAppointments: [],
      isLoadingCoordinatedAppointments: false,
      hasRequestedCoordinatedAppointments: false,
      isHydrating: false,
      isReady: false,
      yesNoOptions: [
        {
          id: 1,
          label: 'Sí',
          value: true,
          check: false,
          name: 'coordinated-evaluation',
        },
        {
          id: 2,
          label: 'No',
          value: false,
          check: false,
          name: 'coordinated-evaluation',
        },
      ],
      allowedStudentStatuses: [
        StudentStatuses.Servido,
        StudentStatuses.Elegible,
      ],
    };
  },
  computed: {
    selectedStudent() {
      return this.form.students[0] ?? null;
    },
    hasCoordinatedAppointments() {
      return this.coordinatedAppointments.length > 0;
    },
    selectedPurposes() {
      return this.purposeOptions.filter((purpose) => purpose.selected);
    },
    selectedOtherPurpose() {
      return this.selectedPurposes.find((purpose) =>
        this.isOtherPurpose(purpose)
      );
    },
    hasEvaluationAnalysisPurpose() {
      return this.selectedPurposes.some(
        (purpose) => purpose.id === EVALUATION_ANALYSIS_PURPOSE_ID
      );
    },
    availablePendingEvaluations() {
      const selectedEvaluationIds = this.selectedEvaluations.map(
        (evaluation) => evaluation.studentEvaluationId
      );

      return this.pendingEvaluations.filter(
        (evaluation) =>
          !selectedEvaluationIds.includes(evaluation.studentEvaluationId)
      );
    },
    schoolObjectKey() {
      return `${this.getObjectId(this.form.school) ?? 'empty'}-${
        this.schools.length
      }`;
    },
    townObjectKey() {
      return `${this.getObjectId(this.form.town) ?? 'empty'}-${
        this.towns.length
      }`;
    },
    readonlyCoordinatedAppointmentLabel() {
      const appointment =
        this.form.coordinatedAppointment ??
        this.initialData?.coordinatedAppointment ??
        this.initialData?.appointment;
      const appointmentId =
        this.form.compuAppointmentId ??
        this.getObjectId(appointment) ??
        this.initialData?.compuAppointmentId;

      return appointment?.name ?? appointment?.text ?? appointmentId ?? '-';
    },
    showNoCoordinatedAppointments() {
      return (
        !this.readonly &&
        Boolean(this.selectedStudent) &&
        this.form.hasCoordinatedAppointment === true &&
        this.hasRequestedCoordinatedAppointments &&
        !this.isLoadingCoordinatedAppointments &&
        !this.hasCoordinatedAppointments
      );
    },
  },
  watch: {
    initialData: {
      immediate: true,
      handler(value) {
        if (value && this.isReady) {
          this.hydrateForm(value);
        }
      },
    },
    selectedStudent(student) {
      if (this.isHydrating) return;

      this.clearCoordinatedAppointments();
      this.clearSelectedEvaluations();

      if (!student) {
        this.clearStudentDependentInformation();
        return;
      }

      this.preselectStudentSchool(student);
      this.loadCoordinatedAppointments();
      this.fetchPendingEvaluations(this.getStudentSieId(student));
    },
    async 'form.academicYear'() {
      if (this.isHydrating || !this.isReady || !this.isActive) return;

      this.form.school = null;
      this.form.town = null;
      await this.fetchLocations();
    },
    async isActive(value) {
      if (
        !value ||
        !this.isReady ||
        this.isHydrating ||
        !this.form.academicYear?.id
      ) {
        return;
      }

      await this.fetchLocations();
    },
    'form.hasCoordinatedAppointment'(value) {
      if (this.isHydrating) return;

      this.setHasCoordinatedAppointmentOption(value);

      const hadSelectedCoordinatedAppointment =
        !!this.form.compuAppointmentId ||
        !!this.getObjectId(this.form.coordinatedAppointment);

      this.clearCoordinatedAppointments();

      if (value === true) {
        this.loadCoordinatedAppointments();
      } else if (value === false && hadSelectedCoordinatedAppointment) {
        this.clearCoordinatedAppointmentPreload();
      }
    },
    form: {
      deep: true,
      handler() {
        if (this.isHydrating) return;

        this.emitForm();
      },
    },
    purposeOptions: {
      deep: true,
      handler() {
        if (this.isHydrating) return;

        this.clearPurposeValidationError();
        this.emitForm();
      },
    },
    participants: {
      deep: true,
      handler() {
        if (this.isHydrating) return;

        this.emitForm();
      },
    },
    selectedEvaluations: {
      deep: true,
      handler() {
        if (this.isHydrating) return;

        if (this.selectedEvaluations.length > 0) {
          this.evaluationValidationError = '';
        }

        this.emitForm();
      },
    },
    hasEvaluationAnalysisPurpose(value) {
      if (!value) {
        this.evaluationValidationError = '';
      }
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.loadAcademicYearAndLocations(),
        this.fetchPurposeOptions(),
      ]);

      this.initializeParticipants(this.initialData?.participants);
      this.isReady = true;

      if (this.initialData) {
        await this.hydrateForm(this.initialData);
      } else if (!this.minuteId) {
        this.$emit('ready');
      }
    } catch (error) {
      this.ShowToast('Error', error.message, 'error');
      this.$emit('ready');
    }
  },
  methods: {
    async loadAcademicYearAndLocations() {
      await this.fetchAcademicYears();

      this.form.academicYear =
        this.resolveAcademicYear(this.initialData) ?? this.form.academicYear;

      if (this.hasInitialLocationData(this.initialData)) {
        this.seedLocationOptions(this.initialData);
      } else if (this.isActive) {
        await this.fetchLocations();
      }
    },
    async validate() {
      const isFormValid = await this.$refs.formValidator.validate();
      const hasValidPurposes = this.validatePurposeSelection();
      const hasValidOtherPurpose = this.validateOtherPurposeDescription();
      const hasValidEvaluations = this.validateEvaluationSelection();

      return (
        isFormValid &&
        hasValidPurposes &&
        hasValidOtherPurpose &&
        hasValidEvaluations
      );
    },
    handleHasCoordinatedAppointmentChange(item) {
      this.form.hasCoordinatedAppointment = item?.value;
      this.setHasCoordinatedAppointmentOption(item?.value);
    },
    setHasCoordinatedAppointmentOption(value) {
      this.yesNoOptions = this.yesNoOptions.map((option) => ({
        ...option,
        check: option.value === value,
      }));
    },
    emitForm() {
      const hasCoordinatedAppointment =
        this.form.hasCoordinatedAppointment === true;

      this.$emit('change', {
        ...this.form,
        compuAppointmentId: hasCoordinatedAppointment
          ? this.form.compuAppointmentId ??
            this.getObjectId(this.form.coordinatedAppointment)
          : null,
        purposes: this.selectedPurposes,
        participants: this.getParticipantsPayload(),
        evaluations: this.hasEvaluationAnalysisPurpose
          ? this.selectedEvaluations.map((evaluation) => ({
              studentEvaluationId: evaluation.studentEvaluationId,
              name: evaluation.name,
              evaluationDate: evaluation.evaluationDate,
            }))
          : [],
        statusId: this.initialData?.statusId,
        statusName: this.initialData?.statusName,
        createdBy: this.initialData?.createdBy,
      });
    },
    clearStudentDependentInformation() {
      this.form.school = null;
      this.form.town = null;
      this.form.appointmentDate = null;
      this.form.hasCoordinatedAppointment = null;
      this.clearCoordinatedAppointments();
      this.clearPurposeSelections();
      this.initializeParticipants();
      this.clearSelectedEvaluations();
      this.pendingEvaluations = [];
    },
    preselectStudentSchool(student) {
      if (this.readonly) return;

      const school = this.normalizeSchoolOption(student?.school);

      if (!school) return;

      this.schools = this.addOptionIfMissing(this.schools, school);
      this.form.school = this.findOptionById(this.schools, school.id) ?? school;
    },
    clearSelectedEvaluations() {
      this.selectedEvaluations = [];
      this.selectedPendingEvaluation = null;
    },
    clearCoordinatedAppointments() {
      this.form.coordinatedAppointment = null;
      this.form.compuAppointmentId = null;
      this.coordinatedAppointments = [];
      this.hasRequestedCoordinatedAppointments = false;
    },
    clearCoordinatedAppointmentPreload() {
      this.form.school = null;
      this.form.town = null;
      this.form.appointmentDate = null;
      this.form.compuAppointmentId = null;
      this.clearPurposeSelections();
      this.initializeParticipants();
      this.clearPurposeValidationError();
      this.clearOtherPurposeValidationError();
      this.removeAppointmentEvaluations();
    },
    clearPurposeSelections() {
      this.purposeOptions = this.purposeOptions.map((purpose) => ({
        ...purpose,
        selected: false,
        otherDescription: '',
      }));
    },
    loadCoordinatedAppointments() {
      if (
        this.readonly ||
        this.form.hasCoordinatedAppointment !== true ||
        !this.selectedStudent
      ) {
        return;
      }

      this.fetchCoordinatedAppointments();
    },
    ensureCoordinatedAppointmentOption(appointment) {
      const appointmentId = this.getObjectId(appointment);

      if (!appointmentId) {
        return appointment;
      }

      const normalizedAppointmentId = String(appointmentId);
      const option =
        this.coordinatedAppointments.find(
          (item) => String(this.getObjectId(item)) === normalizedAppointmentId
        ) ?? {
          ...appointment,
          id: appointmentId,
          name: appointment.name ?? `${appointmentId}`,
        };

      this.coordinatedAppointments = [
        option,
        ...this.coordinatedAppointments.filter(
          (item) => String(this.getObjectId(item)) !== normalizedAppointmentId
        ),
      ];

      return option;
    },
    async hydrateForm(data) {
      this.isHydrating = true;

      try {
        const student = data.student ?? data.selectedStudent ?? null;
        const purposes = data.purposes ?? [];
        const selectedPurposeIds = purposes.map((purposeItem) =>
          this.getSelectedPurposeId(purposeItem)
        );
        let coordinatedAppointment =
          data.coordinatedAppointment ??
          data.appointment ??
          (data.compuAppointmentId
            ? {
                id: data.compuAppointmentId,
                name: `${data.compuAppointmentId}`,
              }
            : null);
        const hasPersistedMinute = Boolean(data.id ?? data.compuMinuteId);
        const hasCoordinatedAppointment =
          typeof data.hasCoordinatedAppointment === 'boolean'
            ? data.hasCoordinatedAppointment
            : coordinatedAppointment
            ? true
            : hasPersistedMinute
            ? false
            : null;
        const academicYear =
          this.resolveAcademicYear(data) ?? this.form.academicYear;

        if (
          academicYear?.id &&
          this.locationsAcademicYearId !== academicYear.id
        ) {
          this.form.academicYear = academicYear;

          if (this.hasResolvedLocationForAcademicYear(academicYear.id)) {
            this.locationsAcademicYearId = academicYear.id;
          } else if (this.hasInitialLocationData(data)) {
            this.seedLocationOptions(data);
          } else if (this.isActive) {
            await this.fetchLocations();
          }
        }

        this.ensureLocationOptions(data);
        const school =
          this.normalizeSchoolOption(data.school) ??
          this.findOptionById(this.schools, data.locationSchoolId);
        const town =
          this.normalizeTownOption(data.town ?? data.municipality) ??
          this.findOptionById(this.towns, data.townId ?? data.municipalityId);

        if (this.readonly && coordinatedAppointment) {
          coordinatedAppointment = this.ensureCoordinatedAppointmentOption(
            coordinatedAppointment
          );
        }

        this.form = {
          ...this.getDefaultForm(),
          students: student ? [student] : [],
          academicYear,
          school,
          town,
          appointmentDate: data.appointmentDate
            ? new Date(data.appointmentDate)
            : null,
          hasCoordinatedAppointment,
          coordinatedAppointment,
          compuAppointmentId:
            data.compuAppointmentId ?? this.getObjectId(coordinatedAppointment),
        };
        this.setHasCoordinatedAppointmentOption(
          this.form.hasCoordinatedAppointment
        );
        this.resolveLocationSelections(data);

        this.purposeOptions = this.purposeOptions.map((purpose) => ({
          ...purpose,
          selected: selectedPurposeIds.includes(purpose.id),
          otherDescription:
            purposes.find(
              (purposeItem) =>
                this.getSelectedPurposeId(purposeItem) === purpose.id
            )?.otherDescription ??
            purpose.otherDescription ??
            '',
        }));
        this.initializeParticipants(data.participants);

        const studentSieId = this.getStudentSieId(student);
        if (studentSieId) {
          await this.fetchPendingEvaluations(studentSieId);
        }

        this.selectedEvaluations = (data.evaluations || []).map(
          (evaluation) => {
            const pendingEvaluation = this.pendingEvaluations.find(
              (item) =>
                item.studentEvaluationId === evaluation.studentEvaluationId
            );

            return {
              studentEvaluationId: evaluation.studentEvaluationId,
              name: pendingEvaluation?.name ?? evaluation.name ?? '',
              evaluationDate:
                pendingEvaluation?.evaluationDate ??
                evaluation.evaluationDate ??
                null,
            };
          }
        );

        if (!this.readonly && student && this.form.hasCoordinatedAppointment) {
          await this.fetchCoordinatedAppointments();
          const coordinatedAppointmentId = this.getObjectId(
            coordinatedAppointment
          );
          this.form.coordinatedAppointment =
            this.coordinatedAppointments.find(
              (appointment) =>
                this.getObjectId(appointment) === coordinatedAppointmentId
            ) ?? coordinatedAppointment;
        }
      } finally {
        this.isHydrating = false;
        this.emitForm();
        this.$emit('ready');
      }
    },
    async fetchAcademicYears() {
      const [academicYearsResponse, currentAcademicYear] = await Promise.all([
        academicYearService.getAcademicYears(),
        academicYearService.getCurrentAcademicYear(),
      ]);
      const academicYears = academicYearsResponse.data ?? [];

      if (!currentAcademicYear) {
        throw new Error('No se pudo cargar el año académico activo.');
      }

      this.academicYears = this.normalizeAcademicYears(
        academicYears,
        currentAcademicYear
      );

      if (!this.form.academicYear) {
        this.form.academicYear =
          this.academicYears.find(
            (academicYear) => academicYear.id === currentAcademicYear.id
          ) ?? this.normalizeAcademicYear(currentAcademicYear);
      }
    },
    async fetchLocations() {
      const academicYearId = this.form.academicYear?.id;

      if (!academicYearId) {
        if (!this.isActive) {
          return;
        }

        throw new Error(
          'No se pudieron cargar las escuelas y pueblos sin un año académico activo.'
        );
      }

      if (!this.isActive) {
        return;
      }

      if (this.locationsFetchedAcademicYearId === academicYearId) {
        return;
      }

      const [schoolsResponse, townsResponse] = await Promise.all([
        locationService.getSchools({ academicYearId }),
        locationService.getMunicipalities({ academicYearId }),
      ]);
      this.schools = this.normalizeLocationOptions(
        schoolsResponse.data,
        this.normalizeSchoolOption
      );
      this.towns = this.normalizeLocationOptions(
        townsResponse.data,
        this.normalizeTownOption
      );
      this.ensureLocationOptions(this.initialData);
      this.ensureLocationOptions({
        school: this.form.school,
        town: this.form.town,
      });
      this.resolveLocationSelections(this.initialData);
      this.locationsAcademicYearId = academicYearId;
      this.locationsFetchedAcademicYearId = academicYearId;
    },
    hasInitialLocationData(data) {
      return Boolean(data?.school && (data?.town || data?.municipality));
    },
    hasResolvedLocationForAcademicYear(academicYearId) {
      return Boolean(
        academicYearId &&
          this.getObjectId(this.form.academicYear) == academicYearId &&
          this.form.school &&
          this.form.town
      );
    },
    seedLocationOptions(data) {
      this.schools = [];
      this.towns = [];
      this.ensureLocationOptions(data);
      this.locationsAcademicYearId =
        this.resolveAcademicYear(data)?.id ??
        data?.academicYearId ??
        data?.configAcademicYearId ??
        this.locationsAcademicYearId;
    },
    ensureLocationOptions(data) {
      const school = this.normalizeSchoolOption(data?.school);
      const town = this.normalizeTownOption(data?.town ?? data?.municipality);

      this.schools = this.addOptionIfMissing(this.schools, school);
      this.towns = this.addOptionIfMissing(this.towns, town);
    },
    normalizeSchoolOption(option) {
      return this.normalizeLocationOption(option, {
        idKeys: ['id', 'schoolId', 'locationSchoolId'],
        nameKeys: ['name', 'schoolName', 'locationSchoolName', 'locationName'],
      });
    },
    normalizeTownOption(option) {
      return this.normalizeLocationOption(option, {
        idKeys: ['id', 'townId', 'municipalityId'],
        nameKeys: ['name', 'townName', 'municipalityName'],
      });
    },
    normalizeLocationOptions(options, normalizer) {
      return (options ?? [])
        .map((option) => normalizer.call(this, option))
        .filter(Boolean);
    },
    normalizeLocationOption(option, { idKeys, nameKeys }) {
      if (!option) {
        return null;
      }

      const baseOption = typeof option === 'object' ? option : {};
      const id =
        idKeys.map((key) => baseOption?.[key]).find((value) => value != null) ??
        option;
      const name =
        nameKeys
          .map((key) => baseOption?.[key])
          .find((value) => value != null && value !== '') ??
        baseOption?.description ??
        baseOption?.label;

      return {
        ...baseOption,
        id,
        name: name ?? baseOption?.name ?? `${id}`,
      };
    },
    addOptionIfMissing(options, option) {
      if (!option) {
        return options;
      }

      const optionId = this.getObjectId(option);

      if (
        optionId &&
        options.some(
          (currentOption) => this.getObjectId(currentOption) == optionId
        )
      ) {
        return options;
      }

      return [...options, option];
    },
    resolveLocationSelections(data = null) {
      const schoolId =
        this.getObjectId(this.form.school) ?? data?.locationSchoolId;
      const townId =
        this.getObjectId(this.form.town) ??
        data?.townId ??
        data?.municipalityId;

      this.form.school =
        this.findOptionById(this.schools, schoolId) ?? this.form.school;
      this.form.town =
        this.findOptionById(this.towns, townId) ?? this.form.town;
    },
    findOptionById(options, id) {
      if (!id) {
        return null;
      }

      return options.find((option) => this.getObjectId(option) == id) ?? null;
    },
    async fetchPurposeOptions() {
      const purposes = await compuMinuteService.fetchPurposes();
      const selectedPurposes = this.initialData?.purposes ?? [];
      const selectedPurposeIds = selectedPurposes.map((purpose) =>
        this.getSelectedPurposeId(purpose)
      );

      this.purposeOptions = (purposes ?? []).map((purpose) => ({
        ...purpose,
        selected: selectedPurposeIds.includes(purpose.id),
        otherDescription:
          selectedPurposes.find(
            (selectedPurpose) =>
              this.getSelectedPurposeId(selectedPurpose) === purpose.id
          )?.otherDescription ?? '',
      }));
    },
    async fetchCoordinatedAppointments() {
      if (this.readonly) {
        return;
      }

      const studentSieId = this.getStudentSieId(this.selectedStudent);

      if (!studentSieId) {
        throw new Error(
          'No se pudo consultar las coordinaciones porque el estudiante no tiene SIE.'
        );
      }

      this.isLoadingCoordinatedAppointments = true;

      try {
        const {
          data: appointments,
        } = await compuAppointmentService.fetchCoordinatedAppointments(
          this.getCoordinatedAppointmentPayload(studentSieId)
        );
        this.coordinatedAppointments = appointments ?? [];
      } catch (error) {
        this.coordinatedAppointments = [];
        this.ShowToast(
          'Error',
          'No se pudieron cargar las coordinaciones del estudiante.',
          'error'
        );
      } finally {
        this.isLoadingCoordinatedAppointments = false;
        this.hasRequestedCoordinatedAppointments = true;
      }
    },
    async handleCoordinatedAppointmentSelection(appointment) {
      if (this.isHydrating || !appointment) return;

      await this.applyCoordinatedAppointmentPreload(appointment);
    },
    async applyCoordinatedAppointmentPreload(appointment) {
      const appointmentId = this.getObjectId(appointment);

      if (!appointmentId) return;

      try {
        const {
          data: preload,
        } = await compuMinuteService.fetchAppointmentPreload(appointmentId);

        this.isHydrating = true;
        const currentAppointment = this.form.coordinatedAppointment;
        const academicYear =
          this.resolveAcademicYear(preload) ?? this.form.academicYear;

        if (academicYear) {
          this.form.academicYear = academicYear;
        }

        this.ensureLocationOptions(preload);
        this.form.school =
          this.normalizeSchoolOption(preload?.school) ??
          this.findOptionById(this.schools, preload?.locationSchoolId) ??
          null;
        this.form.town =
          this.normalizeTownOption(preload?.town ?? preload?.municipality) ??
          this.findOptionById(
            this.towns,
            preload?.townId ?? preload?.municipalityId
          ) ??
          null;
        this.form.appointmentDate = preload?.appointmentDate
          ? new Date(preload.appointmentDate)
          : null;
        this.form.hasCoordinatedAppointment = true;
        this.form.coordinatedAppointment = currentAppointment;
        this.form.compuAppointmentId =
          preload?.compuAppointmentId ?? appointmentId;
        this.applyPurposesFromPreload(preload?.purposes);
        this.applyEvaluationsFromPreload(appointmentId, preload?.evaluations);
      } catch (error) {
        this.ShowToast(
          'Error',
          'No se pudo precargar la información de la coordinación.',
          'error'
        );
      } finally {
        this.isHydrating = false;
        this.emitForm();
      }
    },
    applyPurposesFromPreload(purposes = []) {
      const selectedPurposeIds = (purposes ?? []).map((purposeItem) =>
        this.getSelectedPurposeId(purposeItem)
      );

      this.purposeOptions = this.purposeOptions.map((purpose) => {
        const selectedPurpose = (purposes ?? []).find(
          (purposeItem) => this.getSelectedPurposeId(purposeItem) === purpose.id
        );

        return {
          ...purpose,
          selected: selectedPurposeIds.includes(purpose.id),
          otherDescription: selectedPurpose?.otherDescription ?? '',
        };
      });
    },
    applyEvaluationsFromPreload(appointmentId, evaluations = []) {
      // Solo se reemplazan las evaluaciones cargadas automáticamente desde una
      // invitación anterior; las añadidas manualmente por el funcionario se conservan.
      const manualEvaluations = this.selectedEvaluations.filter(
        (evaluation) => !evaluation.fromCompuAppointmentId
      );

      if (!this.hasEvaluationAnalysisPurpose || !evaluations?.length) {
        this.selectedEvaluations = manualEvaluations;
        return;
      }

      const appointmentEvaluations = evaluations
        .filter(
          (evaluation) =>
            !manualEvaluations.some(
              (existing) =>
                existing.studentEvaluationId === evaluation.studentEvaluationId
            )
        )
        .map((evaluation) => {
          const pendingEvaluation = this.pendingEvaluations.find(
            (item) =>
              item.studentEvaluationId === evaluation.studentEvaluationId
          );

          return {
            studentEvaluationId: evaluation.studentEvaluationId,
            name: pendingEvaluation?.name ?? evaluation.name ?? '',
            evaluationDate:
              pendingEvaluation?.evaluationDate ??
              evaluation.evaluationDate ??
              null,
            fromCompuAppointmentId: appointmentId,
          };
        });

      this.selectedEvaluations = [...manualEvaluations, ...appointmentEvaluations];
    },
    removeAppointmentEvaluations() {
      this.selectedEvaluations = this.selectedEvaluations.filter(
        (evaluation) => !evaluation.fromCompuAppointmentId
      );
    },
    getCoordinatedAppointmentPayload(studentSieId) {
      return { studentSieId };
    },
    getDefaultForm() {
      return {
        students: [],
        academicYear: null,
        school: null,
        town: null,
        appointmentDate: null,
        hasCoordinatedAppointment: null,
        coordinatedAppointment: null,
        compuAppointmentId: null,
      };
    },
    initializeParticipants(existingParticipants = []) {
      const normalizedParticipants = Array.isArray(existingParticipants)
        ? existingParticipants.map((participant) =>
            this.normalizeParticipant(participant)
          )
        : [];
      const predefinedGroups = [1, 5, 4];
      const predefinedParticipants = predefinedGroups.map((groupId) => {
        const existing = normalizedParticipants.find(
          (participant) => participant.groupId === groupId
        );

        return existing ?? this.createParticipant(groupId, false);
      });
      const additionalParticipants = normalizedParticipants.filter(
        (participant) => !predefinedGroups.includes(participant.groupId)
      );

      this.participants = [
        ...predefinedParticipants,
        ...additionalParticipants.map((participant) => ({
          ...participant,
          isRemovable: true,
        })),
      ];
    },
    createParticipant(groupId = 0, isRemovable = true) {
      this.signatureCounter -= 1;

      return {
        id: this.signatureCounter,
        signature: {
          name: '',
          positionId: null,
          participationId: null,
          alternateId: null,
        },
        groupId,
        isRequired: false,
        isRemovable,
        isDisabled: false,
      };
    },
    normalizeParticipant(participant) {
      const groupId = participant.groupId ?? participant.attendanceGroupId ?? 0;

      return {
        id:
          participant.id ??
          participant.participantId ??
          this.signatureCounter--,
        signature: {
          name:
            participant.signature?.name ??
            participant.fullName ??
            participant.name ??
            '',
          positionId: this.normalizePosition(participant),
          participationId: this.normalizeParticipation(participant),
          alternateId: this.normalizeAlternateParticipation(participant),
        },
        groupId,
        isRequired: false,
        isRemovable: participant.isRemovable ?? groupId === 0,
        isDisabled: false,
      };
    },
    normalizePosition(participant) {
      const positionId =
        participant.signature?.positionId?.signaturePositionId ??
        participant.positionId ??
        participant.position?.id ??
        participant.positionCode;

      if (!positionId) {
        return null;
      }

      return (
        this.availableSignatureOptions.find(
          (option) => option.signaturePositionId == positionId
        ) ?? { signaturePositionId: positionId }
      );
    },
    normalizeParticipation(participant) {
      const participationId =
        participant.signature?.participationId?.id ??
        participant.participationType ??
        participant.participationTypeId;

      if (!participationId) {
        return null;
      }

      return (
        this.participationTypeList.find(
          (participationType) => participationType.id == participationId
        ) ?? { id: Number(participationId) }
      );
    },
    normalizeAlternateParticipation(participant) {
      const alternateId =
        participant.signature?.alternateId?.id ??
        participant.alternateParticipationType ??
        participant.alternateParticipationTypeId;

      return alternateId ? { id: Number(alternateId) } : undefined;
    },
    getParticipantsPayload() {
      return this.participants.map((participant) => ({
        id: participant.id,
        groupId: participant.groupId,
        fullName: participant.signature?.name ?? '',
        positionId:
          participant.signature?.positionId?.signaturePositionId ?? null,
        participationTypeId: participant.signature?.participationId?.id ?? null,
        alternateParticipationTypeId:
          participant.signature?.alternateId?.id ?? null,
        isAdditional: participant.isRemovable,
      }));
    },
    normalizeAcademicYears(academicYears, currentAcademicYear) {
      const normalizedAcademicYears = academicYears.map((academicYear) =>
        this.normalizeAcademicYear(academicYear)
      );

      if (
        currentAcademicYear &&
        !normalizedAcademicYears.some(
          (academicYear) => academicYear.id === currentAcademicYear.id
        )
      ) {
        normalizedAcademicYears.unshift(
          this.normalizeAcademicYear(currentAcademicYear)
        );
      }

      return normalizedAcademicYears;
    },
    normalizeAcademicYear(academicYear) {
      return {
        ...academicYear,
        name:
          academicYear.description ??
          academicYear.name ??
          academicYear.year ??
          academicYear.value,
      };
    },
    resolveAcademicYear(data) {
      if (!data) {
        return null;
      }

      const academicYearId =
        data.academicYear?.id ??
        data.academicYearId ??
        data.configAcademicYearId;

      if (!academicYearId) {
        return null;
      }

      return (
        this.academicYears.find(
          (academicYear) => academicYear.id == academicYearId
        ) ??
        (data.academicYear
          ? this.normalizeAcademicYear(data.academicYear)
          : null)
      );
    },
    getSelectedPurposeId(purpose) {
      return purpose?.id ?? purpose;
    },
    validatePurposeSelection() {
      const isValid = this.selectedPurposes.length > 0;
      this.purposeValidationError = isValid
        ? ''
        : 'Selecciona al menos un propósito';

      return isValid;
    },
    validateOtherPurposeDescription() {
      if (!this.selectedOtherPurpose) {
        this.otherPurposeValidationError = '';
        return true;
      }

      const isValid = Boolean(
        this.selectedOtherPurpose.otherDescription?.trim()
      );
      this.otherPurposeValidationError = isValid
        ? ''
        : 'Debe ingresar una descripción';

      return isValid;
    },
    validateEvaluationSelection() {
      if (!this.hasEvaluationAnalysisPurpose) {
        this.evaluationValidationError = '';
        return true;
      }

      const isValid = this.selectedEvaluations.length > 0;
      this.evaluationValidationError = isValid
        ? ''
        : 'Debe seleccionar al menos una evaluación.';

      return isValid;
    },
    handlePurposeInput(purpose, isSelected) {
      this.clearPurposeValidationError();

      if (!isSelected && this.isOtherPurpose(purpose)) {
        purpose.otherDescription = '';
        this.clearOtherPurposeValidationError();
      }
    },
    clearPurposeValidationError() {
      if (this.selectedPurposes.length > 0) {
        this.purposeValidationError = '';
      }
    },
    clearOtherPurposeValidationError() {
      if (this.selectedOtherPurpose?.otherDescription?.trim()) {
        this.otherPurposeValidationError = '';
      }
    },
    async fetchPendingEvaluations(studentSieId) {
      if (!studentSieId) {
        this.pendingEvaluations = [];
        return;
      }

      this.isPendingEvaluationsLoading = true;

      try {
        const {
          data: response,
        } = await compuAppointmentService.fetchPendingEvaluations(
          studentSieId
        );

        this.pendingEvaluations = Array.isArray(response) ? response : [];
      } catch {
        this.pendingEvaluations = [];
      } finally {
        this.isPendingEvaluationsLoading = false;
      }
    },
    addPendingEvaluation() {
      if (!this.selectedPendingEvaluation) return;

      const alreadySelected = this.selectedEvaluations.some(
        (evaluation) =>
          evaluation.studentEvaluationId ===
          this.selectedPendingEvaluation.studentEvaluationId
      );

      if (!alreadySelected) {
        this.selectedEvaluations.push(this.selectedPendingEvaluation);
      }

      this.selectedPendingEvaluation = null;
    },
    removePendingEvaluation(evaluationId) {
      this.selectedEvaluations = this.selectedEvaluations.filter(
        (evaluation) => evaluation.studentEvaluationId !== evaluationId
      );
    },
    formatEvaluationDate(date) {
      if (!date) return '-';

      const formattedDate = moment(date)
        .locale('es')
        .format('DD/MMM/YYYY');

      return formattedDate.replace(
        /\/(.)/,
        (match, letter) => `/${letter.toUpperCase()}`
      );
    },
    isOtherPurpose(purpose) {
      return purpose?.name?.trim().toLowerCase() === 'otro';
    },
    getPurposeColumnClass(purpose) {
      return this.isOtherPurpose(purpose) && purpose.selected
        ? 'col-12'
        : 'col-12 col-md-6';
    },
    getPurposeOptionClass(purpose) {
      return {
        'minute-form__purpose-option--other':
          this.isOtherPurpose(purpose) && purpose.selected,
      };
    },
    getStudentSieId(student) {
      return student?.studentSieId ?? student?.studentId ?? null;
    },
    async searchStudents(searchText) {
      return await compuMinuteService.searchStudents(searchText);
    },
    getObjectId(value) {
      return (
        value?.id ??
        value?.schoolId ??
        value?.locationSchoolId ??
        value?.townId ??
        value?.municipalityId ??
        value?.value ??
        value
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.minute-form {
  padding-top: 1.25rem;

  &__subtitle {
    margin-bottom: 0.5rem;
  }

  &__label {
    display: block;
    margin-bottom: 0.35rem;
    color: #555;
    font-size: 0.85rem;
  }

  &__empty-coordination {
    display: block;
    margin-top: 0.35rem;
    color: #727272;
    font-size: 0.8rem;
  }

  &__readonly-appointment-value {
    min-height: 38px;
    padding: 0.45rem 0.75rem;
    border: 1px solid #c5cdcf;
    border-radius: 4px;
    background: #e9ecef;
    color: #495057;
    line-height: 1.35;
  }

  &__empty-student {
    margin-top: 1.25rem;
  }

  &__empty-student-message {
    margin-top: 1rem !important;
    padding: 0.5rem 0 1.75rem;
  }

  &__purposes {
    row-gap: 0.35rem;
  }

  &__purpose-option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }

  &__purpose-option--other {
    max-width: 620px;
  }

  &__other-purpose {
    flex: 1;
    min-width: 280px;
    max-width: 520px;
  }

  &__compu-participants {
    margin-top: 0.75rem;
  }

  &__evaluations {
    margin-top: 0.75rem;
    padding-top: 0.5rem;
    border-top: 1px solid #dfe4e1;
  }

  &__evaluation-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    align-items: end;
  }

  &__evaluation-select {
    background-color: white;
  }

  &__evaluation-option {
    display: flex;
    flex-direction: column;
    gap: 3px;
    line-height: 1.25;
    padding: 4px 0;
  }

  &__evaluation-option-name {
    color: #11291b;
    font-weight: 400;
  }

  &__evaluation-option-date {
    color: #727272;
    font-size: 13px;
  }

  &__evaluation-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 0.5rem;
  }

  &__evaluation-row-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
    border-radius: 4px;
    min-height: 42px;
    padding: 10px 12px;
    background: #edf4f0;
  }

  &__evaluation-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__evaluation-remove {
    background: transparent;
    border: 0;
    color: #ff5b5c;
    padding: 0;
    line-height: 1;
  }
}

::v-deep .minute-form__evaluation-select .vs__dropdown-toggle {
  min-height: 38px;
}

::v-deep .minute-form__evaluation-select .vs__dropdown-option {
  white-space: normal;
}

::v-deep .minute-form__purpose-option .input-group {
  width: auto;
  flex: 0 0 auto;
}

::v-deep .minute-form__subtitle .title-container {
  background: #edf4f0;
}

::v-deep .minute-form__subtitle--small,
::v-deep .minute-form__subtitle--small *,
::v-deep .minute-form__subtitle--small .title-container,
::v-deep .minute-form__subtitle--small .title-container * {
  font-size: 14px !important;
}
</style>
